import { AnimatePresence, motion } from "framer-motion";
import { BadgePercent, Check, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { effectivePrice, isOnSale, saleEndsIn } from "@/lib/pricing";

export const PackageDetailModal = ({ pkg, onClose, onPurchase }) => {
  if (!pkg) return null;
  const sale = isOnSale(pkg);
  const endsIn = saleEndsIn(pkg);

  return (
    <Dialog open={!!pkg} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="bg-[#0D0B12] border-white/10 text-white sm:max-w-2xl p-0 overflow-hidden"
        data-testid="package-modal"
      >
        <div className="relative aspect-[16/8] overflow-hidden">
          <img src={pkg.image_url} alt={pkg.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B12] via-[#0D0B12]/20 to-transparent" />
          <div className="absolute bottom-4 left-6 flex items-end gap-3">
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight text-white" data-testid="package-modal-name">{pkg.name}</h3>
            {sale && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-black text-[10px] font-bold tracking-[0.15em] mb-1.5">
                <BadgePercent className="w-3 h-3" /> SALE
              </span>
            )}
          </div>
          <div className="absolute bottom-4 right-6 text-right">
            {sale && <span className="block text-sm text-slate-400 line-through">${pkg.price.toFixed(2)}</span>}
            <span className={`font-display text-3xl ${sale ? "text-amber-300" : "text-white"}`} data-testid="package-modal-price">
              ${effectivePrice(pkg).toFixed(2)}{pkg.recurring && <span className="text-sm font-sans text-slate-500">/{pkg.recurring === "month" ? "mo" : pkg.recurring}</span>}
            </span>
            {endsIn && <span className="block text-[10px] font-bold tracking-[0.15em] text-amber-300 mt-1">ENDS IN {endsIn.toUpperCase()}</span>}
          </div>
        </div>

        <div className="p-6 sm:p-8">
          {pkg.description && (
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed whitespace-pre-line" data-testid="package-modal-description">
              {pkg.description}
            </p>
          )}
          {pkg.perks?.length > 0 && (
            <ul className="mt-5 space-y-2">
              {pkg.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#8B5CF6]" strokeWidth={3} />
                  {perk}
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={() => onPurchase(pkg)}
            data-testid="package-modal-purchase-button"
            className="mt-8 w-full py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] shadow-[0_0_30px_rgba(139,92,246,0.35)] transition-[background-color] duration-300"
          >
            PURCHASE — ${effectivePrice(pkg).toFixed(2)}{pkg.recurring ? "/mo" : ""}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
